/**
 * Keyboard access for elements that act on a click but are not buttons: a table row that opens a
 * record, a card that selects something.
 *
 *   <tr tabIndex={0} onClick={open} onKeyDown={onActivateKey(open)} className={`... ${ROW_FOCUS_RING}`}>
 *
 * Enter or Space runs the handler, as they would on a button, but only when the key is pressed on the
 * element itself. A row or card that holds buttons of its own must leave their keys alone: the
 * prescription queue card took Enter from its Call and Start buttons and opened the patient's profile
 * instead, and a role card took it from its "N users" button.
 */
export const onActivateKey = (handler) => (e) => {
  if (e.target !== e.currentTarget) return;
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  handler(e);
};

/**
 * For a <form> that must not be submitted by Enter pressed in one of its fields:
 *
 *   <form onSubmit={save} onKeyDown={keepEnterFromSubmitting}>
 *
 * A browser submits a form when Enter is pressed in one of its inputs. On a long data-entry form a
 * reflex Enter, pressed to move on, became a save: Register Patient submitted half-filled, and Edit
 * saved at once, before the name being typed had been title-cased on leaving the field. Enter still
 * works wherever it means something: on the form's buttons (the submit button included), in a
 * textarea, and in any picker or field that handles Enter itself and has already prevented it.
 */
const BUTTON_LIKE_INPUTS = new Set(['submit', 'button', 'image', 'reset', 'file']);

export const keepEnterFromSubmitting = (e) => {
  if (e.key !== 'Enter' || e.defaultPrevented) return;
  // An input method finishing a composition sends Enter too; that Enter belongs to it.
  if (e.nativeEvent && e.nativeEvent.isComposing) return;
  const el = e.target;
  if (el.tagName !== 'INPUT' || BUTTON_LIKE_INPUTS.has(el.type)) return;
  e.preventDefault();
};

/**
 * Moves focus to the first field inside `container` marked `data-invalid="true"`, in page order, and
 * says whether it found one. Within that field it prefers the box a value is typed into (the number
 * beside a country code picker, say) and otherwise takes the field's picker button.
 *
 * Used where a form's save button disables itself while any field has an error: a focused button that
 * turns disabled drops keyboard focus to the top of the page, which left a keyboard user with no idea
 * where the problem was.
 */
const TYPED_CONTROL = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([readonly]):not(:disabled)',
  'textarea:not([readonly]):not(:disabled)',
].join(', ');
const ANY_CONTROL = [
  'input:not([type="hidden"]):not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  'button:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export const focusFirstInvalidField = (container) => {
  const field = container && container.querySelector('[data-invalid="true"]');
  const control = field && (field.querySelector(TYPED_CONTROL) || field.querySelector(ANY_CONTROL));
  if (!control) return false;
  control.focus();
  return true;
};

/**
 * Focus style for a focusable row: an outline drawn just inside the row plus a tint, shown only for
 * keyboard focus. Inside, so the table or card around the row cannot clip it; the tint as well,
 * because an outline on a table row is easy to miss against the row borders.
 */
export const ROW_FOCUS_RING =
  'outline-none focus-visible:outline-emerald-500 focus-visible:-outline-offset-2 focus-visible:bg-emerald-50';
